// SEQ-180 branch fix: maps refresh_pnl_overtime_monthly()'s error codes
// (supabase/migrations/20260908000400_pnl_overtime_monthly.sql) to the
// message the "Refresh payroll data" button shows an admin. Pure so it can
// be unit tested without a live Supabase call.

export interface RefreshRpcError {
  code?: string | null;
  message?: string | null;
}

/**
 * The exact strings the migration raises, which this maps away from
 * (supabase/migrations/20260908000400_pnl_overtime_monthly.sql):
 *
 *   42501  'authentication required'
 *   42501  'only an organization admin may refresh payroll overtime figures'
 *   P0002  'public.keypay_earnings_lines / public.keypay_payruns are not present...'
 *
 * Design audit, MEDIUM: those reach the user as-is today, which means US
 * spelling ("organization") and a raw table list in a button's error toast.
 * The two the app can explain are now mapped to AU wording written for the
 * person pressing the button.
 */
const DB_MESSAGE_MAP: { match: RegExp; message: string }[] = [
  {
    match: /only an organi[sz]ation admin/i,
    message: "Only an organisation admin can refresh payroll data",
  },
  {
    match: /authentication required/i,
    message: "Sign in again to refresh payroll data",
  },
  {
    match: /keypay_earnings_lines|keypay_payruns/i,
    message: "Payroll data is not connected on this database yet",
  },
];

/**
 * PN001 = throttled (refreshed less than 5 minutes ago).
 * PN002 = a concurrent refresh already holds the advisory lock.
 * Anything the map above recognises gets AU wording written for a user.
 * Anything else is still shown VERBATIM, deliberately: an unexpected refusal
 * must never hide behind a generic message, which is the rule this module
 * was written for in the first place.
 */
export function refreshErrorMessage(error: RefreshRpcError): string {
  if (error.code === "PN001") {
    return "Payroll data was refreshed less than 5 minutes ago";
  }
  if (error.code === "PN002") {
    return "A refresh is already running";
  }

  const raw = error.message ?? "";
  const mapped = DB_MESSAGE_MAP.find((entry) => entry.match.test(raw));
  if (mapped) return mapped.message;

  return raw || "Could not refresh payroll data";
}
